import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import '../legal/legal.css';
import { BrandLogo } from '../legal/illustrations';
import { searchPages, SearchEntry } from '../legal/siteSearch';
import { getUser, onAuthChange, AuthUser } from '../auth/auth';
import { useT, LOCALES } from '../i18n/I18nProvider';

// ─────────────────────────────────────────────────────────────────────────────
// Shared site header for the public/info pages.
//
// On small screens the nav collapses into the existing right-side slide-in drawer
// (hamburger ↔ X). Rendered by SiteLayout, NOT by the screens themselves, so it
// never shows up inside in-app webviews.
// ─────────────────────────────────────────────────────────────────────────────

export const MAIN_NAV = [
    { to: '/events', key: 'nav.discover', priority: 'discover' },
    { to: '/hilfe#events-erstellen', key: 'nav.organize', priority: 'organize' },
    { to: '/tickets', key: 'nav.tickets', priority: 'tickets' },
    { to: '/model', key: 'nav.venues', priority: 'venues' },
    { to: '/hilfe', key: 'nav.help', priority: 'help' },
];

const CATEGORY_GROUPS = [
    {
        key: 'cat.featured',
        items: [
            { id: 1, key: 'cat.music' },
            { id: 2, key: 'cat.nightlife' },
            { id: 3, key: 'cat.festivals' },
            { id: 17, key: 'cat.culture' },
        ],
    },
    {
        key: 'cat.business',
        items: [
            { id: 6, key: 'cat.networking' },
            { id: 7, key: 'cat.workshops' },
            { id: 8, key: 'cat.startups' },
            { id: 19, key: 'cat.education' },
        ],
    },
    {
        key: 'cat.lifestyle',
        items: [
            { id: 9, key: 'cat.food' },
            { id: 11, key: 'cat.fashion' },
            { id: 13, key: 'cat.sports' },
            { id: 14, key: 'cat.wellness' },
        ],
    },
];

const API_BASE = 'https://maineventbackend.seuytel.com/api/';
const BACKEND_ROOT = 'https://maineventbackend.seuytel.com';

const CATEGORY_LOOK = {
    1: { icon: '♪', color: '#7C3AED' },
    2: { icon: '☾', color: '#DB2777' },
    3: { icon: '⚑', color: '#F59E0B' },
    17: { icon: '✦', color: '#8B5CF6' },
    6: { icon: '▣', color: '#2563EB' },
    7: { icon: '✎', color: '#0891B2' },
    8: { icon: '↗', color: '#16A34A' },
    19: { icon: '◈', color: '#4F46E5' },
    9: { icon: '◐', color: '#EA580C' },
    11: { icon: '◆', color: '#E11D48' },
    13: { icon: '●', color: '#059669' },
    14: { icon: '☼', color: '#0D9488' },
} as const;

const CATEGORY_ITEMS = CATEGORY_GROUPS.flatMap((group) => group.items);
const RADIUS_OPTIONS_KM = [5, 10, 25, 50, 100];

type LocationSuggestion = {
    id?: number | string;
    label: string;
    locationName?: string;
    city?: string;
    state?: string;
    country?: string;
    hint?: string;
};

type EventSuggestion = {
    id?: number | string;
    label: string;
    hint?: string;
};

type QuerySuggestion =
    | { kind: 'event'; event: EventSuggestion }
    | { kind: 'page'; page: SearchEntry };

type EventFilters = {
    event_category?: number[];
    date_from?: string;
    date_to?: string;
    min_price?: number;
    max_price?: number;
    is_online?: boolean;
    radius?: number;
};

// Person / login glyph.
const PersonIcon = () => (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
        <circle cx="12" cy="8" r="4" fill="currentColor" />
        <path d="M4 21a8 8 0 0 1 16 0Z" fill="currentColor" />
    </svg>
);

function resolveProfilePhoto(user?: AuthUser | null): string | null {
    const raw = String(
        user?.profile_photo_url ||
        user?.profile_photo_path ||
        user?.profile_photo ||
        user?.avatar ||
        '',
    ).trim();
    if (!raw) return null;
    if (/^(https?:|data:|blob:)/.test(raw)) return raw;
    if (raw.startsWith('/storage/')) return `${BACKEND_ROOT}${raw}`;
    if (raw.startsWith('storage/')) return `${BACKEND_ROOT}/${raw}`;
    if (raw.startsWith('/')) return `${BACKEND_ROOT}${raw}`;
    return `${BACKEND_ROOT}/storage/${raw}`;
}

const SearchGlyph = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
        <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const LocationGlyph = () => (
    <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
        <path
            d="M12 21s7-6.1 7-12a7 7 0 0 0-14 0c0 5.9 7 12 7 12Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
        />
        <circle cx="12" cy="9" r="2.3" fill="currentColor" />
    </svg>
);

const FilterGlyph = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M4 7h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="17" cy="7" r="2.2" fill="none" stroke="currentColor" strokeWidth="2" />
        <path d="M20 17H10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <circle cx="7" cy="17" r="2.2" fill="none" stroke="currentColor" strokeWidth="2" />
    </svg>
);

const CloseGlyph = () => (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
);

const TicketIcon = () => (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path
            d="M4 8.5A2.5 2.5 0 0 1 6.5 6h11A2.5 2.5 0 0 1 20 8.5v1.2a2.3 2.3 0 0 0 0 4.6v1.2a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 15.5v-1.2a2.3 2.3 0 0 0 0-4.6Z"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinejoin="round"
        />
        <path d="M9 8v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="1 3" />
    </svg>
);

function normaliseLocationSuggestion(item: any): LocationSuggestion | null {
    if (typeof item === 'string') {
        const label = item.trim();
        return label ? { label } : null;
    }

    if (!item || typeof item !== 'object') return null;

    const location = item.location && typeof item.location === 'object' ? item.location : item;
    const locationName = location.location_name || item.location_name || '';
    const city = location.city || item.city || '';
    const state = location.state || item.state || '';
    const country = location.country || item.country || '';
    const label = String(city || item.city || '').trim();
    const hint = [state, country].filter(Boolean).join(', ');

    if (!label) return null;

    return {
        id: item.id,
        label,
        locationName,
        city,
        state,
        country,
        hint,
    };
}

function locationPayloadToSuggestions(payload: any): LocationSuggestion[] {
    const raw = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.results)
                ? payload.results
                : Array.isArray(payload?.locations)
                    ? payload.locations
                    : [];

    const seen = new Set<string>();
    const suggestions: LocationSuggestion[] = [];

    for (const item of raw) {
        const suggestion = normaliseLocationSuggestion(item);
        if (!suggestion) continue;
        const key = suggestion.label.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        suggestions.push(suggestion);
    }

    return suggestions.slice(0, 6);
}

function normaliseEventSuggestion(item: any): EventSuggestion | null {
    if (typeof item === 'string') {
        const label = item.trim();
        return label ? { label } : null;
    }

    if (!item || typeof item !== 'object') return null;

    const label = String(item.title || item.name || item.label || '').trim();
    if (!label) return null;

    const location = item.location && typeof item.location === 'object' ? item.location : item;
    const hint = [location.city || item.city, location.country || item.country].filter(Boolean).join(', ');

    return {
        id: item.id,
        label,
        hint,
    };
}

function eventPayloadToSuggestions(payload: any): EventSuggestion[] {
    const raw = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload?.results)
                ? payload.results
                : Array.isArray(payload?.events)
                    ? payload.events
                    : Array.isArray(payload?.data?.events)
                        ? payload.data.events
                        : [];

    const seen = new Set<string>();
    const suggestions: EventSuggestion[] = [];

    for (const item of raw) {
        const suggestion = normaliseEventSuggestion(item);
        if (!suggestion) continue;
        const key = suggestion.label.toLowerCase();
        if (seen.has(key)) continue;
        seen.add(key);
        suggestions.push(suggestion);
    }

    return suggestions.slice(0, 6);
}

function dateInputToStore(value: string | undefined): string | undefined {
    return value ? `${value} 00:00:00` : undefined;
}

function storeDateToInput(value: string | undefined): string {
    return value ? value.split(' ')[0] : '';
}

function filtersFromSearchParams(params: URLSearchParams): EventFilters {
    const categories = [...params.getAll('category[]'), ...params.getAll('category')]
        .map((v) => Number(v))
        .filter((v) => Number.isFinite(v));
    const min = params.get('min_price');
    const max = params.get('max_price');
    const radius = params.get('radius');
    const online = params.get('is_online');

    return {
        ...(categories.length ? { event_category: categories } : {}),
        ...(params.get('date_from') ? { date_from: params.get('date_from') ?? undefined } : {}),
        ...(params.get('date_to') ? { date_to: params.get('date_to') ?? undefined } : {}),
        ...(min !== null && min !== '' ? { min_price: Number(min) } : {}),
        ...(max !== null && max !== '' ? { max_price: Number(max) } : {}),
        ...(online === 'true' ? { is_online: true } : online === 'false' ? { is_online: false } : {}),
        ...(radius !== null && radius !== '' ? { radius: Number(radius) } : {}),
    };
}

function activeFilterCount(filters: EventFilters): number {
    let count = 0;
    if (filters.event_category?.length) count += filters.event_category.length;
    if (filters.date_from || filters.date_to) count += 1;
    if (filters.min_price !== undefined || filters.max_price !== undefined) count += 1;
    if (filters.is_online !== undefined) count += 1;
    if (filters.radius !== undefined) count += 1;
    return count;
}

function appendFiltersToParams(params: URLSearchParams, filters: EventFilters) {
    filters.event_category?.forEach((id) => params.append('category[]', String(id)));
    if (filters.date_from) params.set('date_from', filters.date_from);
    if (filters.date_to) params.set('date_to', filters.date_to);
    if (filters.min_price !== undefined) params.set('min_price', String(filters.min_price));
    if (filters.max_price !== undefined) params.set('max_price', String(filters.max_price));
    if (filters.is_online !== undefined) params.set('is_online', String(filters.is_online));
    if (filters.radius !== undefined) params.set('radius', String(filters.radius));
}

// ── Language / country dropdown ───────────────────────────────────────────────
// Selecting an option switches the whole UI language live via the i18n context.
function LocaleSelector() {
    const { code, setCode, t } = useT();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const current = LOCALES.find((l) => l.code === code) ?? LOCALES[0];

    // Close on outside click.
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    return (
        <div className="se-locale" ref={ref}>
            <button
                type="button"
                className="se-locale-btn"
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                <span className="se-locale-flag" aria-hidden="true">{current.flag}</span>
                <span className="se-locale-code">{current.label}</span>
                <span className="se-locale-caret" aria-hidden="true">▾</span>
            </button>
            {open && (
                <ul className="se-locale-menu" role="listbox" aria-label={t('locale.aria')}>
                    {LOCALES.map((l) => (
                        <li key={l.code}>
                            <button
                                type="button"
                                role="option"
                                aria-selected={l.code === code}
                                className={`se-locale-opt${l.code === code ? ' se-active' : ''}`}
                                onClick={() => {
                                    setCode(l.code);
                                    setOpen(false);
                                }}
                            >
                                <span aria-hidden="true">{l.flag}</span>
                                <span className="se-locale-opt-country">{l.country}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

function EventFilterPopup({
    open,
    filters,
    hasLocation,
    onClose,
    onApply,
    onReset,
}: {
    open: boolean;
    filters: EventFilters;
    hasLocation: boolean;
    onClose: () => void;
    onApply: (filters: EventFilters) => void;
    onReset: () => void;
}) {
    const { t } = useT();
    const [draft, setDraft] = useState<EventFilters>(filters);

    useEffect(() => {
        if (open) setDraft(filters);
    }, [open, filters]);

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', onKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = '';
        };
    }, [open, onClose]);

    if (!open) return null;

    const toggleCategory = (id: number) => {
        setDraft((prev) => {
            const current = prev.event_category ?? [];
            const nextCats = current.includes(id) ? current.filter((cat) => cat !== id) : [...current, id];
            const next = { ...prev };
            if (nextCats.length) next.event_category = nextCats;
            else delete next.event_category;
            return next;
        });
    };

    const update = <K extends keyof EventFilters>(key: K, value: EventFilters[K] | undefined) => {
        setDraft((prev) => {
            const next = { ...prev };
            if (value === undefined || (value as any) === '') delete next[key];
            else next[key] = value;
            return next;
        });
    };

    const priceIsFree = draft.min_price === 0 && draft.max_price === undefined;

    const popup = (
        <div className="se-filter-overlay" role="dialog" aria-modal="true" aria-label={t('filter.title')}>
            <button type="button" className="se-filter-backdrop" aria-label={t('filter.close')} onClick={onClose} />
            <section className="se-filter-panel">
                <div className="se-filter-handle" aria-hidden="true" />
                <header className="se-filter-head">
                    <button type="button" className="se-filter-close" onClick={onClose} aria-label={t('filter.close')}>
                        <CloseGlyph />
                    </button>
                    <h2>{t('filter.title')}</h2>
                    <span className="se-filter-head-spacer" />
                </header>

                <div className="se-filter-body">
                    <section className="se-filter-section">
                        <h3>{t('filter.category')}</h3>
                        <div className="se-filter-cat-grid">
                            {CATEGORY_ITEMS.map((cat) => {
                                const active = draft.event_category?.includes(cat.id) ?? false;
                                const look = CATEGORY_LOOK[cat.id as keyof typeof CATEGORY_LOOK] ?? { icon: '✦', color: '#5B8FE8' };
                                return (
                                    <button
                                        key={cat.id}
                                        type="button"
                                        className={`se-filter-cat${active ? ' se-active' : ''}`}
                                        style={{ ['--cat-color' as any]: look.color }}
                                        onClick={() => toggleCategory(cat.id)}
                                    >
                                        <span className="se-filter-cat-top">
                                            <span className="se-filter-cat-icon" aria-hidden="true">{look.icon}</span>
                                            <span className="se-filter-cat-radio" aria-hidden="true" />
                                        </span>
                                        <span className="se-filter-cat-name">{t(cat.key)}</span>
                                    </button>
                                );
                            })}
                        </div>
                    </section>

                    <section className="se-filter-section">
                        <h3>{t('filter.eventType')}</h3>
                        <div className="se-filter-segment">
                            {[
                                { label: t('filter.allEvents'), value: undefined },
                                { label: t('filter.onlineOnly'), value: true },
                                { label: t('filter.liveOnly'), value: false },
                            ].map((item) => (
                                <button
                                    key={String(item.value)}
                                    type="button"
                                    className={draft.is_online === item.value ? 'se-active' : ''}
                                    onClick={() => update('is_online', item.value as any)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </div>
                        {draft.is_online === true && (
                            <p className="se-filter-note">{t('filter.locationIgnored')}</p>
                        )}
                    </section>

                    {hasLocation && draft.is_online !== true && (
                        <section className="se-filter-section">
                            <h3>{t('filter.radius')}</h3>
                            <div className="se-filter-chip-row">
                                {RADIUS_OPTIONS_KM.map((km) => (
                                    <button
                                        key={km}
                                        type="button"
                                        className={(draft.radius ?? 50) === km ? 'se-active' : ''}
                                        onClick={() => update('radius', km)}
                                    >
                                        {km} km
                                    </button>
                                ))}
                            </div>
                        </section>
                    )}

                    <section className="se-filter-section se-filter-two">
                        <div>
                            <h3>{t('filter.date')}</h3>
                            <div className="se-filter-date-row">
                                <label>
                                    <span>{t('filter.from')}</span>
                                    <input
                                        type="date"
                                        value={storeDateToInput(draft.date_from)}
                                        onChange={(e) => update('date_from', dateInputToStore(e.target.value))}
                                    />
                                </label>
                                <label>
                                    <span>{t('filter.to')}</span>
                                    <input
                                        type="date"
                                        value={storeDateToInput(draft.date_to)}
                                        min={storeDateToInput(draft.date_from)}
                                        onChange={(e) => update('date_to', dateInputToStore(e.target.value))}
                                    />
                                </label>
                            </div>
                        </div>

                        <div>
                            <h3>{t('filter.price')}</h3>
                            <label className="se-filter-free">
                                <input
                                    type="checkbox"
                                    checked={priceIsFree}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setDraft((prev) => {
                                                const next = { ...prev, min_price: 0 };
                                                delete next.max_price;
                                                return next;
                                            });
                                        } else {
                                            setDraft((prev) => {
                                                const next = { ...prev };
                                                delete next.min_price;
                                                return next;
                                            });
                                        }
                                    }}
                                />
                                <span>{t('filter.freeOnly')}</span>
                            </label>
                            {!priceIsFree && (
                                <div className="se-filter-price-row">
                                    <label>
                                        <span>{t('filter.minPrice')}</span>
                                        <input
                                            type="number"
                                            min="0"
                                            inputMode="decimal"
                                            value={draft.min_price ?? ''}
                                            placeholder={t('filter.any')}
                                            onChange={(e) => update('min_price', e.target.value === '' ? undefined : Number(e.target.value))}
                                        />
                                    </label>
                                    <label>
                                        <span>{t('filter.maxPrice')}</span>
                                        <input
                                            type="number"
                                            min="0"
                                            inputMode="decimal"
                                            value={draft.max_price ?? ''}
                                            placeholder="50"
                                            onChange={(e) => update('max_price', e.target.value === '' ? undefined : Number(e.target.value))}
                                        />
                                    </label>
                                </div>
                            )}
                        </div>
                    </section>
                </div>

                <footer className="se-filter-footer">
                    <button type="button" className="se-filter-reset" onClick={() => {
                        setDraft({});
                    }}>
                        {t('filter.reset')}
                    </button>
                    <button type="button" className="se-filter-apply" onClick={() => onApply(draft)}>
                        {t('filter.apply')}
                    </button>
                </footer>
            </section>
        </div>
    );

    return createPortal(popup, document.body);
}

// ── Website search + event-location suggestions ──────────────────────────────
function PageSearch({ idSuffix = '' }: { idSuffix?: string }) {
    const navigate = useNavigate();
    const routeLocation = useLocation();
    const { t } = useT();
    const [q, setQ] = useState('');
    const [locationQ, setLocationQ] = useState('');
    const [pageOpen, setPageOpen] = useState(false);
    const [locationOpen, setLocationOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [pageActive, setPageActive] = useState(0);
    const [locationActive, setLocationActive] = useState(0);
    const [filters, setFilters] = useState<EventFilters>(() => filtersFromSearchParams(new URLSearchParams(routeLocation.search)));
    const [eventResults, setEventResults] = useState<EventSuggestion[]>([]);
    const [eventLoading, setEventLoading] = useState(false);
    const [locationResults, setLocationResults] = useState<LocationSuggestion[]>([]);
    const [locationLoading, setLocationLoading] = useState(false);
    const ref = useRef<HTMLFormElement>(null);

    const pageResults: SearchEntry[] = q.trim() ? searchPages(q) : [];
    const queryResults: QuerySuggestion[] = [
        ...eventResults.map((event) => ({ kind: 'event' as const, event })),
        ...pageResults.map((page) => ({ kind: 'page' as const, page })),
    ].slice(0, 8);
    const filterCount = activeFilterCount(filters);

    useEffect(() => {
        const params = new URLSearchParams(routeLocation.search);
        setFilters(filtersFromSearchParams(params));
        setQ(params.get('query') ?? params.get('q') ?? '');
        setLocationQ(params.get('location') ?? params.get('city') ?? '');
    }, [routeLocation.search]);

    useEffect(() => {
        if (!pageOpen && !locationOpen) return;
        const onDoc = (e: MouseEvent) => {
            if (!ref.current || ref.current.contains(e.target as Node)) return;
            setPageOpen(false);
            setLocationOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [pageOpen, locationOpen]);

    useEffect(() => {
        const term = q.trim();
        if (term.length < 3) {
            setEventResults([]);
            setEventLoading(false);
            return;
        }

        setEventResults([]);
        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            setEventLoading(true);
            try {
                const params = new URLSearchParams({ q: term, query: term, size: '6' });
                const res = await fetch(`${API_BASE}search/events/by-title?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Accept: 'application/json' },
                });
                if (!res.ok) throw new Error(`Event suggestions failed: ${res.status}`);
                const payload = await res.json();
                setEventResults(eventPayloadToSuggestions(payload));
            } catch (error: any) {
                if (error?.name !== 'AbortError') {
                    setEventResults([]);
                }
            } finally {
                if (!controller.signal.aborted) setEventLoading(false);
            }
        }, 300);

        return () => {
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [q]);

    useEffect(() => {
        const term = locationQ.trim();
        if (term.length < 3) {
            setLocationResults([]);
            setLocationLoading(false);
            return;
        }

        const controller = new AbortController();
        const timer = window.setTimeout(async () => {
            setLocationLoading(true);
            try {
                const params = new URLSearchParams({ q: term, query: term, city: term });
                const res = await fetch(`${API_BASE}search/events/by-location?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Accept: 'application/json' },
                });
                if (!res.ok) throw new Error(`Location suggestions failed: ${res.status}`);
                const payload = await res.json();
                setLocationResults(locationPayloadToSuggestions(payload));
            } catch (error: any) {
                if (error?.name !== 'AbortError') {
                    setLocationResults([]);
                }
            } finally {
                if (!controller.signal.aborted) setLocationLoading(false);
            }
        }, 300);

        return () => {
            controller.abort();
            window.clearTimeout(timer);
        };
    }, [locationQ]);

    const go = (entry: SearchEntry) => {
        setQ('');
        setPageOpen(false);
        setLocationOpen(false);
        navigate(entry.to);
    };

    const goEventSuggestion = (entry: EventSuggestion) => {
        setQ(entry.label);
        setPageOpen(false);
        setLocationOpen(false);
        const params = new URLSearchParams({ query: entry.label });
        if (locationQ.trim()) params.set('location', locationQ.trim());
        appendFiltersToParams(params, filters);
        navigate(`/events?${params.toString()}`);
    };

    const goQuerySuggestion = (entry: QuerySuggestion) => {
        if (entry.kind === 'event') {
            goEventSuggestion(entry.event);
        } else {
            go(entry.page);
        }
    };

    const goEventSearch = () => {
        const params = new URLSearchParams();
        const query = q.trim();
        const location = locationQ.trim();

        if (query) params.set('query', query);
        if (location) params.set('location', location);
        appendFiltersToParams(params, filters);

        setPageOpen(false);
        setLocationOpen(false);
        navigate(`/events${params.toString() ? `?${params.toString()}` : ''}`);
    };

    const applyFilters = (nextFilters: EventFilters) => {
        setFilters(nextFilters);
        setFilterOpen(false);
        const params = new URLSearchParams();
        if (q.trim()) params.set('query', q.trim());
        if (locationQ.trim()) params.set('location', locationQ.trim());
        appendFiltersToParams(params, nextFilters);
        navigate(`/events${params.toString() ? `?${params.toString()}` : ''}`);
    };

    const resetFilters = () => {
        setFilters({});
        setFilterOpen(false);
        const params = new URLSearchParams();
        if (q.trim()) params.set('query', q.trim());
        if (locationQ.trim()) params.set('location', locationQ.trim());
        navigate(`/events${params.toString() ? `?${params.toString()}` : ''}`);
    };

    const chooseLocation = (entry: LocationSuggestion) => {
        setLocationQ(entry.label);
        setLocationOpen(false);
        setLocationActive(0);
    };

    const onPageKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (!pageOpen || queryResults.length === 0)) {
            e.preventDefault();
            goEventSearch();
            return;
        }
        if (!pageOpen || queryResults.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setPageActive((a) => Math.min(a + 1, queryResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setPageActive((a) => Math.max(a - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            goQuerySuggestion(queryResults[pageActive] ?? queryResults[0]);
        } else if (e.key === 'Escape') {
            setPageOpen(false);
        }
    };

    const onLocationKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && (!locationOpen || locationResults.length === 0)) {
            e.preventDefault();
            goEventSearch();
            return;
        }
        if (!locationOpen || locationResults.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setLocationActive((a) => Math.min(a + 1, locationResults.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setLocationActive((a) => Math.max(a - 1, 0));
        } else if (e.key === 'Enter') {
            e.preventDefault();
            chooseLocation(locationResults[locationActive] ?? locationResults[0]);
        } else if (e.key === 'Escape') {
            setLocationOpen(false);
        }
    };

    const pageListId = `se-search-list${idSuffix}`;
    const locationListId = `se-location-list${idSuffix}`;

    return (
        <form
            className="se-search"
            ref={ref}
            onSubmit={(e) => {
                e.preventDefault();
                goEventSearch();
            }}
        >
            <div className="se-search-field se-search-field-query">
                <span className="se-search-glyph" aria-hidden="true">
                    <SearchGlyph />
                </span>
                <input
                    type="search"
                    className="se-search-input"
                    placeholder={t('search.placeholder')}
                    aria-label={t('search.aria')}
                    aria-expanded={pageOpen && queryResults.length > 0}
                    aria-controls={pageListId}
                    role="combobox"
                    aria-autocomplete="list"
                    value={q}
                    onChange={(e) => {
                        setQ(e.target.value);
                        setPageActive(0);
                        setPageOpen(true);
                        setLocationOpen(false);
                    }}
                    onFocus={() => {
                        setPageOpen(true);
                        setLocationOpen(false);
                    }}
                    onKeyDown={onPageKeyDown}
                />
            </div>

            <div className="se-search-field se-search-field-location">
                <span className="se-location-glyph" aria-hidden="true">
                    <LocationGlyph />
                </span>
                <input
                    type="search"
                    className="se-search-input se-location-input"
                    placeholder={t('search.locationPlaceholder')}
                    aria-label={t('search.locationAria')}
                    aria-expanded={locationOpen && locationResults.length > 0}
                    aria-controls={locationListId}
                    role="combobox"
                    aria-autocomplete="list"
                    value={locationQ}
                    onChange={(e) => {
                        setLocationQ(e.target.value);
                        setLocationActive(0);
                        setLocationOpen(true);
                        setPageOpen(false);
                    }}
                    onFocus={() => {
                        setLocationOpen(true);
                        setPageOpen(false);
                    }}
                    onKeyDown={onLocationKeyDown}
                />
            </div>

            <button
                type="button"
                className={`se-search-filter-btn${filterCount > 0 ? ' se-active' : ''}`}
                aria-label={t('filter.title')}
                aria-expanded={filterOpen}
                onClick={() => {
                    setPageOpen(false);
                    setLocationOpen(false);
                    setFilterOpen(true);
                }}
            >
                <FilterGlyph />
                {filterCount > 0 && <span className="se-search-filter-badge">{filterCount > 9 ? '9+' : filterCount}</span>}
            </button>

            {pageOpen && (queryResults.length > 0 || eventLoading) && (
                <ul className="se-search-list" id={pageListId} role="listbox">
                    {eventLoading && queryResults.length === 0 ? (
                        <li className="se-search-empty">{t('search.loading')}</li>
                    ) : (
                        queryResults.map((r, i) => (
                            <li key={r.kind === 'event' ? `event-${r.event.id ?? r.event.label}` : `page-${r.page.to}-${r.page.title}`}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={i === pageActive}
                                    className={`se-search-opt${i === pageActive ? ' se-active' : ''}`}
                                    onMouseEnter={() => setPageActive(i)}
                                    onClick={() => goQuerySuggestion(r)}
                                >
                                    <span className="se-search-opt-title">
                                        {r.kind === 'event' ? r.event.label : r.page.title}
                                    </span>
                                    <span className="se-search-opt-hint">
                                        {r.kind === 'event' ? (r.event.hint || t('search.eventHint')) : r.page.hint}
                                    </span>
                                </button>
                            </li>
                        ))
                    )}
                </ul>
            )}

            {locationOpen && locationQ.trim().length >= 3 && (locationResults.length > 0 || locationLoading) && (
                <ul className="se-search-list se-location-list" id={locationListId} role="listbox">
                    {locationLoading && locationResults.length === 0 ? (
                        <li className="se-search-empty">{t('search.loading')}</li>
                    ) : (
                        locationResults.map((r, i) => (
                            <li key={`${r.id ?? r.label}-${r.label}`}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={i === locationActive}
                                    className={`se-search-opt${i === locationActive ? ' se-active' : ''}`}
                                    onMouseEnter={() => setLocationActive(i)}
                                    onClick={() => chooseLocation(r)}
                                    >
                                        <span className="se-search-opt-title">{r.label}</span>
                                        {r.hint && <span className="se-search-opt-hint">{r.hint}</span>}
                                    </button>
                            </li>
                        ))
                    )}
                </ul>
            )}

            <EventFilterPopup
                open={filterOpen}
                filters={filters}
                hasLocation={Boolean(locationQ.trim())}
                onClose={() => setFilterOpen(false)}
                onApply={applyFilters}
                onReset={resetFilters}
            />
        </form>
    );
}

export default function Header() {
    const { pathname } = useLocation();
    const { t } = useT();
    const [open, setOpen] = useState(false);
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [searchOpen, setSearchOpen] = useState(false);
    const [hidden, setHidden] = useState(false);
    const [user, setUser] = useState<AuthUser | null>(() => getUser());
    const categoryRef = useRef<HTMLDivElement>(null);
    const lastScrollYRef = useRef(0);

    // Close the drawer whenever the route changes.
    useEffect(() => {
        setOpen(false);
        setCategoryOpen(false);
        setSearchOpen(false);
    }, [pathname]);

    // Keep the login icon in sync with auth state.
    useEffect(() => onAuthChange(() => setUser(getUser())), []);

    // Lock body scroll while the drawer is open.
    useEffect(() => {
        document.body.style.overflow = open ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [open]);

    useEffect(() => {
        const onDoc = (e: MouseEvent) => {
            if (categoryRef.current && !categoryRef.current.contains(e.target as Node)) {
                setCategoryOpen(false);
            }
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    useEffect(() => {
        const onScroll = () => {
            const nextY = window.scrollY || 0;
            const previousY = lastScrollYRef.current;
            const delta = nextY - previousY;

            if (nextY < 24 || open) {
                setHidden(false);
            } else if (delta > 8 && nextY > 120) {
                setHidden(true);
            } else if (delta < -8) {
                setHidden(false);
            }

            lastScrollYRef.current = nextY;
        };

        lastScrollYRef.current = window.scrollY || 0;
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [open]);

    const isActive = (to: string) => (pathname === to ? 'se-active' : undefined);

    const accountLabel = user
        ? t('person.account', { name: user.first_name ?? '' }).trim()
        : t('person.signin');
    const profilePhoto = resolveProfilePhoto(user);

    return (
        <>
            <header className={`se-topbar${hidden ? ' se-topbar-hidden' : ''}`}>
                <div className="se-topbar-inner se-container">
                    <div className="se-topbar-row se-topbar-bar">
                        <Link to="/" className="se-brand" aria-label="MainEvents">
                            <BrandLogo className="se-brand-logo" />
                        </Link>

                        <div className="se-topbar-right">
                            <div className="se-header-main-block">
                                <PageSearch />

                                <button
                                    type="button"
                                    className={`se-search-toggle${searchOpen ? ' se-active' : ''}`}
                                    aria-label={searchOpen ? t('search.close') : t('search.open')}
                                    aria-expanded={searchOpen}
                                onClick={() => {
                                    setCategoryOpen(false);
                                    setSearchOpen((v) => !v);
                                }}
                                >
                                    {searchOpen ? <CloseGlyph /> : <SearchGlyph />}
                                </button>

                                <Link to="/tickets" className="se-ticket-quick" aria-label={t('nav.tickets')}>
                                    <TicketIcon />
                                    <span>{t('nav.tickets')}</span>
                                </Link>

                                <nav className="se-topbar-links se-nav-desktop">
                                    <div className="se-category-menu" ref={categoryRef}>
                                        <button
                                            type="button"
                                            className={`se-category-trigger${categoryOpen ? ' se-active' : ''}`}
                                            aria-haspopup="true"
                                            aria-expanded={categoryOpen}
                                            onClick={() => {
                                                setSearchOpen(false);
                                                setCategoryOpen((v) => !v);
                                            }}
                                        >
                                            {t('nav.categories')}
                                            <span aria-hidden="true">▾</span>
                                        </button>
                                        {categoryOpen && (
                                            <div className="se-category-panel" role="menu">
                                                {CATEGORY_GROUPS.map((group) => (
                                                    <section key={group.key} className="se-category-group">
                                                        <h3>{t(group.key)}</h3>
                                                        <div className="se-category-grid">
                                                            {group.items.map((item) => (
                                                                <Link
                                                                    key={item.id}
                                                                    to={`/events?category=${item.id}`}
                                                                    onClick={() => setCategoryOpen(false)}
                                                                >
                                                                    {t(item.key)}
                                                                </Link>
                                                            ))}
                                                        </div>
                                                    </section>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                    {MAIN_NAV.map((n) => (
                                        <Link
                                            key={n.to}
                                            to={n.to}
                                            className={`${isActive(n.to) ?? ''} se-nav-${n.priority}`.trim()}
                                        >
                                            {t(n.key)}
                                        </Link>
                                    ))}
                                </nav>
                            </div>

                            <div className="se-header-actions">
                                <LocaleSelector />

                                <Link
                                    to={user ? '/profile' : `/login?redirect=${encodeURIComponent('/profile')}`}
                                    className={`se-person${user ? ' se-person-in' : ''}`}
                                    aria-label={accountLabel}
                                    title={accountLabel}
                                >
                                    <div className="se-person-icon" aria-hidden="true">
                                        {profilePhoto ? (
                                            <img className="se-person-avatar" src={profilePhoto} alt="" />
                                        ) : (
                                            <PersonIcon />
                                        )}
                                    </div>
                                    <span className="se-person-label">{user ? (user.first_name || t('person.myAccount')) : t('person.signin')}</span>
                                </Link>

                                {/* Single toggle (small screens only): hamburger ↔ X */}
                                <button
                                    type="button"
                                    className={`se-burger${open ? ' se-open' : ''}`}
                                    aria-label={open ? t('drawer.close') : t('drawer.open')}
                                    aria-expanded={open}
                                    onClick={() => setOpen((v) => !v)}
                                >
                                    <span />
                                    <span />
                                    <span />
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className={`se-search-dock${searchOpen ? ' se-open' : ''}`}>
                        <PageSearch idSuffix="-dock" />
                    </div>
                </div>
            </header>

            {/* Slide-in drawer + scrim — siblings of the header (not nested inside
                it) so they escape the header's backdrop-filter stacking context. */}
            <div
                className={`se-drawer-scrim${open ? ' se-open' : ''}`}
                onClick={() => setOpen(false)}
                aria-hidden={!open}
            >
                <aside
                    className={`se-drawer${open ? ' se-open' : ''}`}
                    role="dialog"
                    aria-label={t('drawer.nav')}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="se-drawer-head">
                        <Link to="/" onClick={() => setOpen(false)}>
                            <BrandLogo className="se-brand-logo" />
                        </Link>
                        <button
                            type="button"
                            className="se-drawer-close"
                            aria-label={t('drawer.close')}
                            onClick={() => setOpen(false)}
                        >
                            ✕
                        </button>
                    </div>

                    <div className="se-drawer-search">
                        <PageSearch idSuffix="-drawer" />
                    </div>

                    <nav className="se-drawer-links">
                        <div className="se-drawer-section-title">{t('drawer.main')}</div>
                        {MAIN_NAV.map((n) => (
                            <Link key={n.to} to={n.to} className={isActive(n.to)} onClick={() => setOpen(false)}>
                                {t(n.key)}
                            </Link>
                        ))}
                        <Link to="/login" className={isActive('/login')} onClick={() => setOpen(false)}>
                            {user ? t('person.myAccount') : t('person.signin')}
                        </Link>

                        <div className="se-drawer-section-title">{t('nav.categories')}</div>
                        {CATEGORY_GROUPS.map((group) => (
                            <div key={group.key} className="se-drawer-cat-group">
                                <strong>{t(group.key)}</strong>
                                {group.items.map((item) => (
                                    <Link
                                        key={item.id}
                                        to={`/events?category=${item.id}`}
                                        onClick={() => setOpen(false)}
                                    >
                                        {t(item.key)}
                                    </Link>
                                ))}
                            </div>
                        ))}
                    </nav>
                </aside>
            </div>
        </>
    );
}
